import { initRoutes } from '@eidellev/adonis-stardust/client';
initRoutes();

import { QuantitySelector } from './modules/quantity-selector';
import { formatPrice, formatPriceNumber } from '../../app/Libraries/Utils';
import type QuantityBreak from '../../app/Models/MillbankAPI/QuantityBreak';
import type Pricing from '../../app/Models/MillbankAPI/Pricing';
import { CustomSelect } from './modules/custom-dropdowns';
import { getCustomerId } from './modules/utils';
import { stardust } from '@eidellev/adonis-stardust/client';
import EcommerceAnalytics from './modules/ecommerce-analytics';

const ecommerceAnalytics = new EcommerceAnalytics();

import tippy from 'tippy.js';

import './modules/pdp-gallery';

document.addEventListener('DOMContentLoaded', async () => {
  const priceContainer = document.getElementById('js-pdp-price');
  const priceValueEl = priceContainer?.querySelector<HTMLElement>('.pdp__price-value');
  const perUnitEl = priceContainer?.querySelector<HTMLElement>('.pdp__price-per-unit');
  const messageContainer = document.querySelector<HTMLElement>('.js-pdp-messages');

  // Async load the live price data if customer is logged in
  const customerId = getCustomerId();
  if (customerId) {
    await fetchPrice();
  }

  const atcBtn = document.querySelector<HTMLButtonElement>('.js-atc') as HTMLButtonElement;

  let quantitySelectorInstance: QuantitySelector | null = null;

  const quantitySelector = document.querySelector<HTMLElement>('.js-quantity-selector');
  if (quantitySelector) {
    quantitySelectorInstance = new QuantitySelector(quantitySelector);

    // Disable atc button if past max stock level

    quantitySelector.addEventListener('quantityChange', (e: CustomEvent) => {
      const qty = e.detail.quantity;
      const input = e.detail.input as HTMLInputElement;
      // Treat absent/empty max as unlimited (e.g. canBeOverSold items have no max attribute)
      const max = input.max !== '' ? parseInt(input.max, 10) : Infinity;

      if (qty <= max && messageContainer) {
        messageContainer.innerHTML = '';
      }

      atcBtn.disabled = qty > max;
    });

    quantitySelector.addEventListener('quantityMaxExceeded', (e: any) => {
      const input = e.detail.input as HTMLInputElement;
      const max = parseInt(input.max, 10);

      if (messageContainer) {
        messageContainer.innerHTML = `
        <p class="pdp__message pdp__message--invalid">
          You have exceeded the total available amount of <strong>${max}</strong>.
        </p>
      `;
      }

      atcBtn.disabled = true;
    });
  }

  const oos = document.querySelectorAll<HTMLElement>('[data-out-of-stock]');
  tippy(oos, {
    arrow: true,
    placement: 'bottom',
    content: 'Out of stock',
    offset: [0, 10],
  });

  const variantSelects = document.querySelectorAll<HTMLElement>('.js-variant-select');
  variantSelects.forEach((select) => {
    new CustomSelect(select as HTMLElement);

    select.addEventListener('custom-select:change', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const slug = detail.value;
      if (atcBtn) {
        atcBtn.disabled = true;
      }

      if (slug) {
        window.location.href = stardust.route('shop.product', { slug });
      }
    });
  });

  const quantityBreak = document.querySelector<HTMLElement>('.js-quantity-break');
  if (quantityBreak) {
    const inputs = quantityBreak.querySelectorAll<HTMLInputElement>('input[name="quantityBreak"]');

    inputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        e.preventDefault();

        updatePrice({
          quantity: input.dataset.quantity || '1',
          basePrice: input.dataset.price || '0',
          discountPrice: input.dataset.discount || '0',
          stock: quantitySelector?.dataset.stock || '0',
        });

        updatePromoSurfaces(input.dataset.uom);
      });
    });

    // Sync the promo badge/callout to whichever quantity break is selected on load.
    const checkedBreak = quantityBreak.querySelector<HTMLInputElement>(
      'input[name="quantityBreak"]:checked'
    );
    updatePromoSurfaces(checkedBreak?.dataset.uom);
  }

  function updatePrice(params: {
    quantity: string | number,
    basePrice: string | number,
    discountPrice: string | number,
    stock?: string | number,
  }) {
    if (!priceContainer || !priceValueEl || !perUnitEl) {
      return;
    }

    const quantity = Number(params.quantity);
    const rawBasePrice = Number(params.basePrice);
    const discountPrice = Number(params.discountPrice);
    const priceToUse = discountPrice ? discountPrice : rawBasePrice;
    // Default compareAt is 0, which will be skipped as falsey
    const compareAt = discountPrice ? rawBasePrice : 0;
    const stockLevel = params.stock ? Number(params.stock) : 0;

    priceValueEl.innerHTML = formatPriceForPage(priceToUse);
    perUnitEl.innerHTML = formatPerUnit(priceToUse, quantity);

    // Add/remove the strikethrough price element based on if the product is on sale
    let priceStrikethroughEl = priceContainer.querySelector('.pdp__price-strikethrough');
    if (compareAt) {
      const strikethroughMarkup = formatPrice(compareAt, 'usd', true);

      if (priceStrikethroughEl) {
        priceStrikethroughEl.innerHTML = strikethroughMarkup;
      } else {
        priceStrikethroughEl = document.createElement('span');
        priceStrikethroughEl.classList.add('pdp__price-strikethrough');
        priceStrikethroughEl.innerHTML = strikethroughMarkup;
        priceValueEl.insertAdjacentElement('afterend', priceStrikethroughEl);
      }
    } else {
      // If no compareAt, remove the strikethrough element if it exists
      priceStrikethroughEl?.remove();
    }

    priceContainer.classList.toggle('pdp__price--sale', !!compareAt);
    priceContainer.classList.remove('pdp__price--loading');

    if (quantitySelectorInstance) {
      // Only update max when one was originally set — absent max means no ceiling (e.g. canBeOverSold)
      const input = quantitySelector?.querySelector<HTMLInputElement>('.quantity__input');
      if (input && input.max !== '') {
        // TODO: This will need to be updated for if we don't have a stockLevel. Right now it defaults to newMax = 0...
        const newMax = Math.floor(stockLevel / quantity);
        quantitySelectorInstance.updateMax(newMax);
      }
    }
  }

  // Show the promo surfaces (badge pill + callout) for the selected quantity break's UOM,
  // hiding the others. No-op on pages with no UOM-grouped promos.
  function updatePromoSurfaces(uom: string | undefined): void {
    document.querySelectorAll<HTMLElement>('.js-promo-uom-group').forEach((group) => {
      group.hidden = group.dataset.uom !== uom;
    });
  }

  async function fetchPrice() {
    try {
      const productId = document.querySelector('.pdp-main[data-product-id]')?.getAttribute('data-product-id');
      if (!productId || Number.isNaN(Number.parseInt(productId, 10))) {
        return;
      }

      // Collect variant product IDs from the card selector so we can bundle them into the same request
      const variantIds = Array.from(
        document.querySelectorAll<HTMLElement>('.product-variant__option[data-product-id]')
      )
        .map((el) => el.getAttribute('data-product-id'))
        .filter((id): id is string => !!id && !Number.isNaN(Number.parseInt(id, 10)) && id !== productId);

      // Related-products slider cards are SSR'd with Algolia/base prices, so bundle
      // their ids into this same request to refresh them with customer pricing.
      const relatedIds = Array.from(
        document.querySelectorAll<HTMLInputElement>('.products-slider .product-card input[name="id"]')
      )
        .map((input) => input.value)
        .filter((id) => !!id && !Number.isNaN(Number.parseInt(id, 10)));

      const allIds = Array.from(new Set([productId, ...variantIds, ...relatedIds]));
      const priceResponse = await fetch(stardust.route('api.products.pricing', {}, { qs: { id: allIds } }));
      if (!priceResponse.ok) {
        throw new Error(`Failed pricing request with status ${priceResponse.status}`);
      }

      const priceDataArray = await priceResponse.json();
      if (!Array.isArray(priceDataArray) || priceDataArray.length === 0) {
        clearPriceLoadingState();
        return;
      }

      const mainPriceData = priceDataArray.find((d: any) => String(d.productId) === productId);
      if (!mainPriceData) {
        clearPriceLoadingState();
        return;
      }

      updatePrice({
        quantity: mainPriceData.price.uom.quantity ?? 1,
        basePrice: mainPriceData.price.base ?? 0,
        discountPrice: mainPriceData.price.discount ?? 0,
        stock: quantitySelector?.dataset.stock ?? 0,
      });

      if (mainPriceData.quantityBreaks) {
        updateQuantityBreakDisplays(mainPriceData);
      }

      if (variantIds.length > 0) {
        updateVariantPrices(priceDataArray);
      }

      if (relatedIds.length > 0) {
        updateRelatedProductPrices(priceDataArray);
      }
    } catch (err) {
      // TODO: add error handling
      console.error('Error fetching price data for PDP:', err);
    } finally {
      clearPriceLoadingState();
    }
  }

  function clearPriceLoadingState() {
    document.querySelector('.pdp__price--loading')?.classList.remove('pdp__price--loading');
    document.querySelectorAll<HTMLElement>(
      '.quantity-break__price--loading, .product-variant__option-price--loading'
    ).forEach((el) => {
      el.classList.remove('quantity-break__price--loading', 'product-variant__option-price--loading');
    });
  }

  /** Update card-style variant option price displays from fetched pricing */
  function updateVariantPrices(priceDataArray: { productId: number; price: Pricing }[]) {
    priceDataArray.forEach((priceData) => {
      const optionEl = document.querySelector<HTMLElement>(
        `.product-variant__option[data-product-id="${priceData.productId}"]`
      );
      if (!optionEl) {
        return;
      }

      const priceEl = optionEl.querySelector<HTMLElement>('.product-variant__option-price');
      if (!priceEl) {
        return;
      }

      const base = priceData.price.base ?? 0;
      const discount = priceData.price.discount ?? 0;

      if (discount) {
        priceEl.classList.add('product-variant__option-price--sale');
        priceEl.innerHTML = `<span>$${formatPriceNumber(discount)}</span><span>$${formatPriceNumber(base)}</span>`;
      } else {
        priceEl.classList.remove('product-variant__option-price--sale');
        priceEl.innerHTML = `<span>$${formatPriceNumber(base)}</span>`;
      }
      priceEl.classList.remove('product-variant__option-price--loading');
    });
  }

  /** Update related-products slider card prices from already-fetched pricing data */
  function updateRelatedProductPrices(priceDataArray: { productId: number; price: Pricing }[]) {
    const priceById = new Map(priceDataArray.map((d) => [String(d.productId), d.price]));

    document.querySelectorAll<HTMLElement>('.products-slider .product-card').forEach((card) => {
      const id = card.querySelector<HTMLInputElement>('input[name="id"]')?.value;
      const cardPrice = id ? priceById.get(id) : undefined;
      const priceEl = card.querySelector<HTMLElement>('.price');
      if (!cardPrice || !priceEl) {
        return;
      }

      const base = cardPrice.base ?? 0;
      const discount = cardPrice.discount ?? 0;
      const [dollars, cents] = formatPriceNumber(discount || base).split('.');

      // Mirrors the SSR product-card / browse.ts price markup: the strikethrough is
      // a sibling above .price__current, not nested inside it.
      priceEl.classList.toggle('price--sale', !!discount);
      priceEl.innerHTML = `
        ${discount ? `<s>${formatPrice(base, 'usd', true)}</s>` : ''}
        <div class="price__current">
          <sup class="price__dollar-sign">$</sup>${dollars}<sup class="price__cents">.${cents}</sup>
          <span class="price__text">each</span>
        </div>`;
    });
  }

  /** Update quantity-break option displays from fetched pricing */
  function updateQuantityBreakDisplays(priceData: { price: Pricing; quantityBreaks?: QuantityBreak[]; }) {
    const singleInputId = 'quantity-break-single';
    const singleInput = document.getElementById(singleInputId);
    if (singleInput) {
      singleInput.dataset.price = String(priceData.price.base ?? 0);
      singleInput.dataset.discount = String(priceData.price.discount ?? 0);
      singleInput.dataset.quantity = String(priceData.price.uom.quantity ?? 1);
      const labelEl = document.querySelector<HTMLElement>(`label[for="${singleInputId}"] .quantity-break__option-name`);
      labelEl!.innerText = priceData.price.uom.label;
      const priceEl = document.querySelector<HTMLElement>(`label[for="${singleInputId}"] .quantity-break__price`);
      priceEl!.innerHTML = formatQuantityBreakPrice(
        priceData.price.base ?? 0,
        priceData.price.discount ?? 0,
      );
    }

    const quantityBreaks = priceData.quantityBreaks ?? [];
    quantityBreaks.forEach((qb) => {
      const qbInputId = `quantity-break-${qb.id}`;
      const qbInput = document.getElementById(qbInputId);
      if (qbInput) {
        qbInput.dataset.price = String(qb.base ?? 0);
        qbInput.dataset.discount = String(qb.discount ?? 0);
        qbInput.dataset.quantity = String(qb.uom.quantity ?? 1);
        const labelEl = document.querySelector<HTMLElement>(`label[for="${qbInputId}"] .quantity-break__option-name`);
        labelEl!.innerText = qb.uom.label;
        const priceEl = document.querySelector<HTMLElement>(`label[for="${qbInputId}"] .quantity-break__price`);
        priceEl!.innerHTML = formatQuantityBreakPrice(
          qb.base ?? 0,
          qb.discount ?? 0,
        );
      }
    });

    document.querySelectorAll<HTMLElement>('.quantity-break__price.quantity-break__price--loading').forEach((el) => {
      el.classList.remove('quantity-break__price--loading');
    });
  }

  ecommerceAnalytics.trackPageView('product');
});

function formatPriceForPage(price: number): string {
  const [dollars, cents] = formatPriceNumber(price).split('.');
  return `<sup>$</sup>${dollars}<sup>.${cents}</sup>`;
};

function formatPerUnit(price: number, quantity: number): string {
  if (quantity <= 1) {
    return '';
  }
  return `<span class="per-unit">($${formatPriceNumber(price / quantity)}/unit)</span>`;
};

function formatQuantityBreakPrice(price: number, salePrice?: number) {
  if (salePrice) {
    return `
      <span class="quantity-break__option-price-discount">$${formatPriceNumber(salePrice)}</span>
      <s>$${formatPriceNumber(price)}</s>
    `;
  }
  return `$${formatPriceNumber(price)}`;
};
